import { Component, Object3D } from '@wonderlandengine/api';
import { property } from '@wonderlandengine/api/decorators.js';
import { Cursor } from '@wonderlandengine/components';
import { EventsClient } from './events-client.js';
import Logger from './utils/logger.js';

const MODULE = 'XRPointerDiagnostics';

type CursorRuntimeLike = {
    collisionGroup?: number;
    _collisionMask?: number;
    rayCastMode?: number | string;
    hoveringObject?: Object3D | null;
};

/**
 * Runtime diagnostics for XR cursor/raycast setup.
 *
 * Attach this to any always-active object. It reports the active cursor
 * components, their configured collision group, resolved collision mask and
 * current hover target while in XR.
 */
export class XRPointerDiagnostics extends Component {
    static TypeName = 'xr-pointer-diagnostics';

    @property.object()
    leftCursorObject: Object3D | null = null;

    @property.object()
    rightCursorObject: Object3D | null = null;

    @property.bool(false)
    logPeriodically = false;

    @property.float(2.0)
    logIntervalSeconds = 2.0;

    private inXR = false;
    private elapsedSinceLog = 0;

    onActivate(): void {
        this.engine.onXRSessionStart.add(this.onXRSessionStart);
        this.engine.onXRSessionEnd.add(this.onXRSessionEnd);
    }

    onDeactivate(): void {
        this.engine.onXRSessionStart.remove(this.onXRSessionStart);
        this.engine.onXRSessionEnd.remove(this.onXRSessionEnd);
    }

    update(dt: number): void {
        if (!this.inXR || !this.logPeriodically) {
            return;
        }

        this.elapsedSinceLog += dt;
        if (this.elapsedSinceLog >= Math.max(0.1, this.logIntervalSeconds)) {
            this.elapsedSinceLog = 0;
            this.logCursorDiagnostics('periodic');
        }
    }

    private onXRSessionStart = (): void => {
        this.inXR = true;
        this.elapsedSinceLog = 0;
        this.logCursorDiagnostics('xr-start');
    };

    private onXRSessionEnd = (): void => {
        this.inXR = false;
    };

    private logCursorDiagnostics(reason: string): void {
        const cursors = this.resolveCursors();
        if (cursors.length === 0) {
            Logger.warn(MODULE, `[${reason}] No cursor components found on configured hand objects.`);
            return;
        }

        const lines: string[] = [];
        for (const item of cursors) {
            const cursor = item.cursor;
            const group = typeof cursor.collisionGroup === 'number' ? cursor.collisionGroup : -1;
            const computedMask = group >= 0 ? (1 << group) : -1;
            const runtimeMask = typeof cursor._collisionMask === 'number' ? cursor._collisionMask : -1;
            const hoverName = cursor.hoveringObject?.name ?? 'none';
            lines.push(
                `${item.label}: group=${group} computedMask=${computedMask} runtimeMask=${runtimeMask} rayCastMode=${String(cursor.rayCastMode ?? 'n/a')} hover=${hoverName}`,
            );
        }

        Logger.warn(MODULE, `[${reason}] ${lines.join(' | ')}`);
    }

    private resolveCursors(): Array<{ label: string; cursor: CursorRuntimeLike }> {
        const results: Array<{ label: string; cursor: CursorRuntimeLike }> = [];
        const seen = new Set<CursorRuntimeLike>();

        const eventsClient = EventsClient.instance;
        const leftObj = this.leftCursorObject ?? eventsClient?.activeLeftHandObject ?? null;
        const rightObj = this.rightCursorObject ?? eventsClient?.activeRightHandObject ?? null;

        this.pushCursor(results, seen, leftObj, 'left');
        this.pushCursor(results, seen, rightObj, 'right');

        return results;
    }

    private pushCursor(
        out: Array<{ label: string; cursor: CursorRuntimeLike }>,
        seen: Set<CursorRuntimeLike>,
        object: Object3D | null,
        label: string,
    ): void {
        if (!object) {
            return;
        }

        const cursor = object.getComponent(Cursor) as unknown as CursorRuntimeLike | null;
        if (!cursor || seen.has(cursor)) {
            return;
        }

        seen.add(cursor);
        out.push({ label, cursor });
    }
}
